import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { NavbarImpersonationExit } from "@/components/ImpersonationBanner";
import {
  PUBLIC_LINKS,
  AUTHED_LINKS,
  ADMIN_LINK,
  AuthedDesktopLinks,
  PublicDesktopLinks,
  DesktopActions,
  MobileMenu,
} from "@/components/navbar/parts";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    // Navigate FIRST so ProtectedRoute doesn't redirect to /login mid-logout.
    navigate("/", { replace: true });
    setOpen(false);
    await logout();
  };

  const isAuthed = !!user;
  const isAdmin = isAuthed && (user.role || "").toLowerCase() === "admin";
  const authedLinks = isAdmin ? [...AUTHED_LINKS, ADMIN_LINK] : AUTHED_LINKS;

  return (
    <header
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/60"
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link
          to={isAuthed ? "/dashboard" : "/"}
          className="flex items-center gap-2 text-slate-900"
          data-testid="nav-brand-link"
        >
          <BrandLogo size="md" tone="light" testid="nav-brand-logo" />
          <span className="font-medium tracking-tight text-base lg:text-lg">
            The AI Took My Job
          </span>
        </Link>

        {isAuthed ? (
          <AuthedDesktopLinks links={authedLinks} />
        ) : (
          <PublicDesktopLinks links={PUBLIC_LINKS} />
        )}

        <DesktopActions user={user} onLogout={onLogout} />

        {/* Impersonation Exit pill — only renders when admin is viewing as
            a client AND the main banner has been hidden. */}
        <NavbarImpersonationExit />

        <button
          type="button"
          aria-label="Toggle menu"
          className="lg:hidden inline-flex items-center justify-center rounded-lg p-2 text-slate-700 hover:bg-slate-100 transition-colors"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle-button"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <MobileMenu
        open={open}
        isAuthed={isAuthed}
        onClose={() => setOpen(false)}
        onLogout={onLogout}
      />
    </header>
  );
}
